import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import * as fromApp from "../../../store/app.reducer";
import * as _ from "lodash";
import { auditTime, distinctUntilChanged, first, sample } from "rxjs/operators";
import { Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import * as SelectedPaymentMethodIDActions from "./store/selectePaymentMethod.actions";
import { SelectStorePopupComponent } from "src/app/popups/select-store-popup/select-store-popup.component";
import { ModalController } from "@ionic/angular";
import { PaymentGatewaysService } from "src/app/services/payment-gateways/payment-gateways.service";
import * as selectors from "../../../store/app.selectors";
import * as CompleteOrderValidationActions from "../../../store/complete-order-validation/completeOrderValidation.actions";
import { ReservationSelectPaymentMethodModalComponent } from "src/app/reservation-system/reservation-select-payment-method-modal/reservation-select-payment-method-modal.component";
import { DineinTableLabelService } from "../../../services/dinein-table-label/dinein-table-label.service";
import { DineinTableLabelContext } from "../../../services/dinein-table-label/dinein-table-label.helpers";
import {
  applyMobileorderPaymentDisplayLabels,
  getMoBaseLanguage,
} from "src/app/services/payment-gateways/payment-method-label.helpers";

// [1248h533uva] Test mobile exempt from the new-customer cash anti-prank gate in
// every store (deny knob and cap). Risk accepted by the operator: it is the
// shared public demo login. Exact string match only — mirrors the servers.
const NEW_CUSTOMER_CASH_EXEMPT_MOBILES: readonly string[] = ["6945000000"];

@Component({
  selector: "app-payment-methods",
  templateUrl: "./payment-methods.component.html",
  styleUrls: ["./payment-methods.component.scss"],
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  public cartPaymentGateWays: any;
  public storesByStoreId: any;
  public paymentGateways: any;
  public cart: any;
  public currentUser: any;
  public currentStoreId: string;
  public customerInvoiceEnabled: boolean;
  public subscriptions: Subscription[] = [];
  public selectedPaymentMethodID: any;
  public displaySelectedPaymentMethodID: any;
  public displaySelectedPaymentMethodLabel: string | null;
  public finalPriceLoading: boolean;
  public disableAllPaymentSelections: boolean;
  public valid: boolean = true;
  public darkMode: boolean;
  public storeInfo: any;
  public groupApp: any;
  public selectedLanguage: string;
  public moBaseLanguage: string = "el";
  public loginState: any;
  public numberOfDisplaydPaymentMethods: number = 0;
  public dineIn;
  public orderPickup;
  public selectedDineInTable;
  public filteredCartPaymentGateWays;
  public storeNetworkConnected: any;
  public customerOrders: any;
  public paymentMethodOrder = [
    "cash",
    "cash_paymentSettlementPending",
    "pre_cash",
    "mobilePOS",
    "pre_mobilePOS",
  ];
  public resolvedPaymentMethodOrder = _.cloneDeep(this.paymentMethodOrder);
  public grandTotal: number;

  private selectedTheme: any;
  private lastValidTimestamp: any;
  private checkboxPromotions: any;
  private stores: any;
  private reservationAppSelectionModal;
  private paymentSettlementPendingStorageKey =
    "paymentSettlementPendingSelected";
  private paymentSettlementPendingMethodID = "cash_paymentSettlementPending";

  public dineinTableContext: DineinTableLabelContext = null;
  private dineinTableContextSub: Subscription;

  constructor(
    private store: Store<fromApp.AppState>,
    private translateService: TranslateService,
    private modalCtrl: ModalController,
    private paymentGatewaysService: PaymentGatewaysService,
    private dineinTableLabelService: DineinTableLabelService
  ) {}

  ngOnInit() {
    this.dineinTableContextSub =
      this.dineinTableLabelService.context$.subscribe((context) => {
        this.dineinTableContext = context;
      });
    this.subscriptions.push(
      this.store
        .select(selectors.getStoreInfo)
        .pipe(distinctUntilChanged())
        .pipe(auditTime(50))
        .subscribe((storeInfo) => {
          if (storeInfo && !_.isEqual(this.storeInfo, storeInfo)) {
            this.storeInfo = _.cloneDeep(storeInfo);
            this.updatePaymentMethodOrder();
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("groupApp")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.groupApp &&
            !_.isEqual(state.groupApp, this.groupApp)
          ) {
            this.groupApp = _.cloneDeep(state.groupApp);
            this.moBaseLanguage = getMoBaseLanguage(this.groupApp);
            this.applyPaymentGatewayDisplayLabels();
          }

          if (
            state &&
            state.selectedLangugage &&
            !_.isEqual(state.selectedLangugage, this.selectedLanguage)
          ) {
            this.selectedLanguage = _.cloneDeep(state.selectedLangugage);
            this.applyPaymentGatewayDisplayLabels();
          }

          if (
            state &&
            state.selectedTheme &&
            !_.isEqual(this.selectedTheme, state.selectedTheme)
          ) {
            this.selectedTheme = _.cloneDeep(state.selectedTheme);
            //console.log("selected theme", this.selectedTheme);
            if (this.selectedTheme === "dark") {
              this.darkMode = true;
            } else {
              this.darkMode = false;
            }
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("completeOrderValidation")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.lastValidTimestamp &&
            !_.isEqual(this.lastValidTimestamp, state.lastValidTimestamp)
          ) {
            this.lastValidTimestamp = _.cloneDeep(state.lastValidTimestamp);
            this.paymentMethodValidationsCart(true);
          }
        })
    );

    this.subscriptions.push(
      this.store.select("disableFinalPrice").subscribe((state) => {
        if (
          state &&
          !_.isEqual(state.disableFinalPrice, this.finalPriceLoading)
        ) {
          this.finalPriceLoading = _.cloneDeep(state.disableFinalPrice);
        }
      })
    );

    this.subscriptions.push(
      this.store.select("selectedPaymentMethodID").subscribe((state) => {
        if (
          state &&
          !_.isEqual(state.selectedPaymentMethodID, this.selectedPaymentMethodID)
        ) {
          this.selectedPaymentMethodID = _.cloneDeep(
            state.selectedPaymentMethodID
          );
          this.syncDisplaySelectedPaymentMethodID();
          this.paymentMethodValidationsCart(false);
        }
      })
    );

    this.subscriptions.push(
      this.store
        .select("auth")
        .pipe(auditTime(200))
        .pipe(distinctUntilChanged())
        .subscribe(async (state) => {
          if (state && !_.isEqual(this.currentUser, state.user)) {
            this.currentUser = _.cloneDeep(state.user);
          }

          if (state && !_.isEqual(this.customerOrders, state.customerOrders)) {
            // New/returning signal for the new-customer cash gate. Re-filter so
            // cash is hidden once we learn the order history is empty.
            this.customerOrders = _.cloneDeep(state.customerOrders);
            if (this.cartPaymentGateWays) {
              await this.calculateFilteredCartPaymentGateways();
            }
          }

          if (
            state &&
            state.loginState &&
            !_.isEqual(this.loginState, state.loginState)
          ) {
            this.loginState = _.cloneDeep(state.loginState);

            if (this.cartPaymentGateWays) {
              if (
                this.loginState &&
                (this.loginState === "no_login" ||
                  (this.loginState === "login_completed" && this.currentUser))
              ) {
                await this.calculateFilteredCartPaymentGateways();
                this.calculateNumberOfDIsplayedPaymentMethods();
              }
            }
          }

          if (
            state &&
            !_.isEqual(this.storeNetworkConnected, state.storeNetworkConnected)
          ) {
            this.storeNetworkConnected = _.cloneDeep(
              state.storeNetworkConnected
            );
            if (this.cartPaymentGateWays) {
              if (
                this.loginState &&
                (this.loginState === "no_login" ||
                  (this.loginState === "login_completed" && this.currentUser))
              ) {
                await this.calculateFilteredCartPaymentGateways();
                this.calculateNumberOfDIsplayedPaymentMethods();
              }
            }
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("cartPrices")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(50))
        .subscribe(async (state) => {
          if (
            state &&
            state.cartPrices &&
            !_.isEqual(state.cartPrices.grand_total, this.grandTotal)
          ) {
            this.grandTotal = _.cloneDeep(
              this.parseAmount(state.cartPrices.grand_total)
            );
            if (
              this.cartPaymentGateWays &&
              this.loginState &&
              (this.loginState === "no_login" ||
                (this.loginState === "login_completed" && this.currentUser))
            ) {
              await this.calculateFilteredCartPaymentGateways();
              this.calculateNumberOfDIsplayedPaymentMethods();
            }
          }
        })
    );

    this.subscriptions.push(
      this.store.select("stores").subscribe((state) => {
        if (state && state.stores && !_.isEqual(state.stores, this.stores)) {
          this.stores = _.cloneDeep(state.stores);
          this.storesByStoreId = _.groupBy(this.stores, "store_id");
          _.each(this.storesByStoreId, (store, key, list) => {
            this.storesByStoreId[key] = store[0];
          });
        }
      })
    );

    this.subscriptions.push(
      this.store.select("currentStore").subscribe((state) => {
        if (
          state &&
          state.currentStoreId &&
          !_.isEqual(state.currentStoreId, this.currentStoreId)
        ) {
          this.currentStoreId = _.cloneDeep(state.currentStoreId);
        }
      })
    );

    this.subscriptions.push(
      this.store.select("deliveryMethodAddress").subscribe(async (state) => {
        if (state && !_.isEqual(state.dineIn, this.dineIn)) {
          this.dineIn = _.cloneDeep(state.dineIn);
          if (this.cartPaymentGateWays) {
            if (
              this.loginState &&
              (this.loginState === "no_login" ||
                (this.loginState === "login_completed" && this.currentUser))
            ) {
              await this.calculateFilteredCartPaymentGateways();
              this.calculateNumberOfDIsplayedPaymentMethods();
            }
          }
        }
        if (state && !_.isEqual(state.orderPickup, this.orderPickup)) {
          this.orderPickup = _.cloneDeep(state.orderPickup);
          if (this.cartPaymentGateWays) {
            if (
              this.loginState &&
              (this.loginState === "no_login" ||
                (this.loginState === "login_completed" && this.currentUser))
            ) {
              await this.calculateFilteredCartPaymentGateways();
              this.calculateNumberOfDIsplayedPaymentMethods();
            }
          }
        }
        // 86ca6v2wr re-filter payment methods when the dine-in table changes
        // (table-level blocks). Same _.isEqual guard prevents recompute on
        // unrelated deliveryMethodAddress mutations (this subscription is not
        // distinctUntilChanged-piped). Trigger only; the authority is
        // calculateFilteredCartPaymentGateways which reads the live deliveryMethod.
        if (
          state &&
          !_.isEqual(state.selectedDineInTable, this.selectedDineInTable)
        ) {
          this.selectedDineInTable = _.cloneDeep(state.selectedDineInTable);
          if (this.cartPaymentGateWays) {
            if (
              this.loginState &&
              (this.loginState === "no_login" ||
                (this.loginState === "login_completed" && this.currentUser))
            ) {
              await this.calculateFilteredCartPaymentGateways();
              this.calculateNumberOfDIsplayedPaymentMethods();
            }
          }
        }
      })
    );

    this.subscriptions.push(
      this.store
        .select("paymentGateWays")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(50))
        .subscribe(async (state) => {
          if (
            state &&
            state.paymentGateWays &&
            !_.isEqual(this.paymentGateways, state.paymentGateWays)
          ) {
            this.paymentGateways = _.cloneDeep(state.paymentGateWays);
            //  console.log("state payment gateways", this.paymentGateways);
          }
          if (
            state &&
            state.cartPaymentGateWays &&
            !_.isEqual(this.cartPaymentGateWays, state.cartPaymentGateWays)
          ) {
            this.cartPaymentGateWays = _.cloneDeep(state.cartPaymentGateWays);

            if (
              this.loginState &&
              (this.loginState === "no_login" ||
                (this.loginState === "login_completed" && this.currentUser))
            ) {
              await this.calculateFilteredCartPaymentGateways();
              this.calculateNumberOfDIsplayedPaymentMethods();
            }
            // console.log("carttt payment gateways", this.paymentGateways);
          }
        })
    );
    this.checkForOnlineOnlyPaymentPromos();
  }

  calculateNumberOfDIsplayedPaymentMethods() {
    this.numberOfDisplaydPaymentMethods = 0;
    for (const property in this.filteredCartPaymentGateWays) {
      if (
        this.filteredCartPaymentGateWays &&
        this.filteredCartPaymentGateWays[property] &&
        this.filteredCartPaymentGateWays[property].active &&
        (!this.filteredCartPaymentGateWays[property].adminOnly ||
          (this.currentUser && this.currentUser.user.admin)) &&
        (!this.filteredCartPaymentGateWays[property].customerOnly ||
          (this.currentUser?.user &&
            ((this.currentUser.user.admin &&
              this.filteredCartPaymentGateWays[property].adminOnly) ||
              (this.currentUser.user.customerPaymentMethods &&
                this.currentUser.user.customerPaymentMethods[
                  this.getPaymentMethodSourceKey(
                    this.filteredCartPaymentGateWays[property],
                    property
                  )
                ])))) &&
        !(
          this.currentUser?.user?.disabledPaymentMethods &&
          this.currentUser.user.disabledPaymentMethods[
            this.getPaymentMethodSourceKey(
              this.filteredCartPaymentGateWays[property],
              property
            )
          ]
        )
      ) {
        this.numberOfDisplaydPaymentMethods++;
      }
    }

    console.log(
      "number of displayed payment methods",
      this.numberOfDisplaydPaymentMethods
    );
  }

  private calculateDeliveryMethod(
    orderPickup: boolean,
    dineIn: boolean
  ): string {
    if (dineIn) return "dinein";
    if (orderPickup) return "takeaway";
    return "delivery";
  }

  calculateFilteredCartPaymentGateways() {
    return new Promise((res) => {
      this.store
        .select("deliveryMethodAddress")
        .pipe(first())
        .subscribe((state) => {
          const deliveryMethod = this.calculateDeliveryMethod(
            state.orderPickup,
            state.dineIn
          );

          // 86ca6v2wr per-table payment block. dine-in only -> delivery/takeaway
          // get [] and stay byte-identical even though selectedDineInTable persists
          // in state after switching away from dine-in.
          const tableDisabledPaymentMethodIds =
            deliveryMethod === "dinein" &&
            Array.isArray(state.selectedDineInTable?.disabledPaymentMethodIds)
              ? state.selectedDineInTable.disabledPaymentMethodIds
              : [];

          _.each(this.cartPaymentGateWays, (method, key) => {
            this.checkPaymentGatewayForDeliveryMethod(
              method,
              deliveryMethod,
              _.cloneDeep(this.currentUser),
              this.storeNetworkConnected,
              key,
              tableDisabledPaymentMethodIds
            );
          });

          this.applyMaxAmountMessages();
          this.applyMinAmountMessages();
          this.applyPrePaymentOverrides();

          this.filteredCartPaymentGateWays = _.cloneDeep(
            this.cartPaymentGateWays
          );
          this.addPaymentSettlementPendingMethod();
          this.applyPaymentGatewayDisplayLabels();
          this.updatePaymentMethodOrder();

          this.store
            .select("selectedPaymentMethodID")
            .pipe(first())
            .subscribe((selectedPaymentMethodState) => {
              const selectedPaymentMethodID =
                selectedPaymentMethodState.selectedPaymentMethodID;
              const selectedPaymentMethod = selectedPaymentMethodID
                ? this.filteredCartPaymentGateWays[selectedPaymentMethodID]
                : null;
              const selectedPaymentMethodIsInvalid =
                !selectedPaymentMethod ||
                selectedPaymentMethod.disable_payment_gateway ||
                selectedPaymentMethod.networkErrMessage ||
                !this.checkPaymentGatewayForDeliveryMethod(
                  selectedPaymentMethod,
                  deliveryMethod,
                  this.currentUser,
                  this.storeNetworkConnected,
                  selectedPaymentMethodID,
                  tableDisabledPaymentMethodIds
                );

              if (
                !selectedPaymentMethodID ||
                (selectedPaymentMethodID && selectedPaymentMethodIsInvalid)
              ) {
                let foundValidMethod = false;
                _.each(
                  this.filteredCartPaymentGateWays,
                  (paymentGateway, key) => {
                    if (
                      this.filteredCartPaymentGateWays &&
                      this.filteredCartPaymentGateWays[key] &&
                      this.filteredCartPaymentGateWays[key].active &&
                      !this.filteredCartPaymentGateWays[key]
                        .disable_payment_gateway &&
                      !this.filteredCartPaymentGateWays[key]
                        .networkErrMessage &&
                      !(
                        this.currentUser?.user?.disabledPaymentMethods &&
                        this.currentUser.user.disabledPaymentMethods[
                          this.getPaymentMethodSourceKey(
                            this.filteredCartPaymentGateWays[key],
                            key
                          )
                        ]
                      ) &&
                      this.checkPaymentGatewayForDeliveryMethod(
                        this.filteredCartPaymentGateWays[key],
                        deliveryMethod,
                        this.currentUser,
                        this.storeNetworkConnected,
                        key,
                        tableDisabledPaymentMethodIds
                      ) &&
                      (!this.filteredCartPaymentGateWays[key].adminOnly ||
                        (this.currentUser && this.currentUser.user.admin)) &&
                      (!this.filteredCartPaymentGateWays[key].customerOnly ||
                        (this.currentUser?.user &&
                          ((this.currentUser.user.admin &&
                            this.filteredCartPaymentGateWays[key].adminOnly) ||
                            (this.currentUser.user.customerPaymentMethods &&
                              this.currentUser.user.customerPaymentMethods[
                                this.getPaymentMethodSourceKey(
                                  this.filteredCartPaymentGateWays[key],
                                  key
                                )
                              ]))))
                    ) {
                      console.log("asdasd", paymentGateway);
                      foundValidMethod = true;
                      this.store.dispatch(
                        new SelectedPaymentMethodIDActions.SetSelectedPaymentMethodID(
                          _.cloneDeep(key)
                        )
                      );
                      this.selectedPaymentMethodID = _.cloneDeep(key);

                      window.localStorage.setItem(
                        "selectedPaymentMethodID",
                        JSON.stringify(key)
                      );

                      return false;
                    }
                  }
                );

                // 86ca6v2wr all-blocked dine-in table: if the previously selected
                // method is now invalid and nothing else is selectable, clear the
                // selection so checkout validation (which only checks a falsy id)
                // blocks the order instead of submitting a blocked method. The
                // backend rejects it as a backstop too.
                if (
                  !foundValidMethod &&
                  selectedPaymentMethodID &&
                  selectedPaymentMethodIsInvalid
                ) {
                  this.store.dispatch(
                    new SelectedPaymentMethodIDActions.SetSelectedPaymentMethodID(
                      null
                    )
                  );
                  this.selectedPaymentMethodID = null;
                  window.localStorage.setItem(
                    "selectedPaymentMethodID",
                    JSON.stringify(null)
                  );
                }
              }

              this.syncDisplaySelectedPaymentMethodID();
            })
            .unsubscribe();

          res({});
        })
        .unsubscribe();
    });
  }

  checkPaymentGatewayForDeliveryMethod(
    method,
    deliveryMethod,
    user,
    isNetworkConnected,
    key?,
    disabledPaymentMethodIds?
  ) {
    // Reset any previous error messages
    console.log(
      "checkPaymentGatewayForDeliveryMethod",
      method,
      deliveryMethod,
      user,
      isNetworkConnected,
      key
    );

    delete method.deliveryMethodErrMsg;
    delete method.networkErrMessage;
    delete method.newCustomerCashErrMsg;
    delete method.newCustomerCashCapAmount;
    method.hideForDeliveryMethod = false;

    // 86ca6v2wr per-table payment block: only for dine-in and only when the table
    // lists this gateway key. Use the source key so synthetic variants (e.g.
    // cash_paymentSettlementPending -> cash) inherit the block. Reset above runs
    // first so a no-longer-blocked method clears its stale message.
    const tableBlockKey = this.getPaymentMethodSourceKey(method, key);
    if (
      deliveryMethod === "dinein" &&
      tableBlockKey &&
      Array.isArray(disabledPaymentMethodIds) &&
      disabledPaymentMethodIds.indexOf(tableBlockKey) !== -1
    ) {
      method.deliveryMethodErrMsg =
        "cart.cart-components.payment-methods.not-available-for-this-table";
      return false;
    }

    const isOnlyDelivery = method.only_delivery;
    const isOnlyTakeaway = method.only_takeaway;
    const isOnlyDinein = method.only_dinein;
    const isDineInVisitor =
      user?.only_visitor && user?.visitor_mode === "dinein";
    const isTakeawayVisitor =
      user?.only_visitor && user?.visitor_mode === "takeaway";
    const isDeliveryVisitor =
      user?.only_visitor && user?.visitor_mode === "delivery";

    const allowed =
      (isOnlyDelivery && deliveryMethod === "delivery") ||
      (isOnlyTakeaway && deliveryMethod === "takeaway") ||
      (isOnlyDinein && deliveryMethod === "dinein");

    const isExclusive = isOnlyDelivery || isOnlyTakeaway || isOnlyDinein;

    if (isExclusive && !allowed) {
      if (key === "pre_cash" || key === "pre_mobilePOS") {
        method.hideForDeliveryMethod = true;
        return false;
      }
      const allowedModes = [];
      if (isOnlyDelivery) allowedModes.push("delivery");
      if (isOnlyTakeaway) allowedModes.push("take-away");
      if (isOnlyDinein) allowedModes.push("dine-in");

      const errorKey =
        "cart.cart-components.payment-methods.only-for-" +
        allowedModes.join("-and-");
      method.deliveryMethodErrMsg = errorKey;
      return false;
    }

    // Anti-prank: limit the pay-on-delivery methods CASH and MOBILEPOS for a NEW
    // customer (no fulfilled prior order) on take-away / delivery. Composable
    // knobs on paymentGateways.cash resolved against the platform default (allow
    // new-customer cash, cap 50). Dine-in is exempt. Server-authoritative in
    // dm-mo-create-order and changeOrderPaymentMethod; this is the UX gate.
    //
    // 86cb5c2rq: mobilePOS is gated too, matching the servers'
    // UNCOLLECTED_PAYMENT_METHODS = ["cash", "mobilePOS"] (86camhjzk). Raw keys
    // only: pre_mobilePOS and the *_pos terminal gateways are NOT gated, and
    // getPaymentMethodSourceKey is deliberately not used. The knobs are always
    // read from paymentGateways.cash, also for mobilePOS, because that is where
    // both servers read them; mobilePOS carries no new-customer knobs of its own.
    //
    // 86cb07qhh: the method is NO LONGER hidden — it stays listed with an
    // explanatory message and is disabled, exactly like the only_delivery /
    // deliveryMethodErrMsg case, because a silently vanishing payment method
    // left both customer and store with no explanation. It is also skipped
    // unless the store offers a usable non-cash alternative, mirroring the
    // server's hasNonCashAlternative guard (previously client-side missing, so
    // a cash-only store hid cash even though the server would have accepted it).
    //
    // Deliberately evaluated BEFORE the three mode-audience branches below: each
    // of those returns on every path, so a cash gateway carrying its own
    // only_delivery / only_takeaway lock used to exit before ever reaching this
    // gate — the customer saw cash fully enabled and only learned of the cap from
    // the create-order rejection. The server applies the cap regardless of the
    // method's own mode flags (dm-mo-create-order gates purely on
    // selectedPaymentMethodID plus the exemptions), so this must run first. The
    // mode MISMATCH case is still handled above and returns before we get here.
    //
    // Exemptions mirror the server: dm-mo-create-order wraps the whole block in
    // `event.mobile && !visitorContext` and then checks !customer.uposMoUser &&
    // !customer.only_dinein && !dinein_table_verified; changeOrderPaymentMethod
    // checks !order.dinein instead. The client can only see the selected mode.
    // cash_paymentSettlementPending is a _.cloneDeep of the cash method shown
    // ONLY to uposMoUser, so it inherits whatever is set here. Visitor pooled
    // fake mobiles are reusable slot identities with no real history, so a
    // visitor would otherwise be quoted a cap the server would never enforce.
    // NEW_CUSTOMER_CASH_EXEMPT_MOBILES (the demo login, risk accepted by the
    // operator) is exempt as on the servers. [1248h533uva]
    // See dm-mobileorder-brain docs/21.
    if (
      (key === "cash" || key === "mobilePOS") &&
      deliveryMethod !== "dinein" &&
      !user?.uposMoUser &&
      !user?.only_dinein &&
      !user?.only_visitor &&
      !NEW_CUSTOMER_CASH_EXEMPT_MOBILES.includes(String(user?.mobile)) &&
      this.isNewCustomerWithNoFulfilledOrder() &&
      this.hasNonCashAlternative(deliveryMethod)
    ) {
      const DEFAULT_ALLOW_NEW_CUSTOMER_CASH = true;
      // Must match DEFAULT_NEW_CUSTOMER_CASH_CAP in dm-mo-create-order and
      // changeOrderPaymentMethod. [86cb5c2rq]
      const DEFAULT_NEW_CUSTOMER_CASH_CAP = 50;
      // cartPaymentGateWays is a cloneDeep of store_info.paymentGateways, the
      // same object the servers read as cashCfg.
      const cashCfg = this.cartPaymentGateWays?.cash || {};
      const allowNewCustomerCash =
        cashCfg.mo_allow_new_customer_cash ?? DEFAULT_ALLOW_NEW_CUSTOMER_CASH;
      // Comma-tolerant parse mirrors the server (parseAmount) so a "20,50" cap
      // is honored identically instead of becoming NaN via Number(...).
      const newCustomerCashCap = this.parseAmount(
        cashCfg.mo_max_amount_new_customer ?? DEFAULT_NEW_CUSTOMER_CASH_CAP
      );
      // this.grandTotal is already parseAmount(cartPrices.grand_total); the
      // existing cartPrices subscription re-filters as it crosses the cap.
      const total = this.grandTotal;

      if (!allowNewCustomerCash) {
        method.newCustomerCashErrMsg =
          "cart.cart-components.payment-methods.new-customer-cash-not-allowed";
        return false;
      }

      // Fails open while the total is not yet available — the server still
      // enforces. The resolved cap is stored on the method so the template can
      // bind a plain value instead of recomputing it during change detection.
      if (
        newCustomerCashCap !== null &&
        newCustomerCashCap > 0 &&
        total !== null &&
        total !== undefined &&
        total > newCustomerCashCap
      ) {
        method.newCustomerCashCapAmount = newCustomerCashCap;
        method.newCustomerCashErrMsg =
          "cart.cart-components.payment-methods.new-customer-cash-over-max-amount";
        return false;
      }
    }

    if (isOnlyDinein && deliveryMethod === "dinein") {
      if (user?.only_dinein || isDineInVisitor) {
        const visitor = method.dinein_visitors || {};
        if (visitor.all_visitors) return true;
        if (visitor.only_network_visitors && isNetworkConnected) return true;

        method.networkErrMessage = visitor.only_network_visitors
          ? "cart.cart-components.payment-methods.must-connect-to-network"
          : "cart.cart-components.payment-methods.not-available-for-visitors";
        return true;
      } else {
        const userObj = method.dinein_users || {};
        if (userObj.all_users) return true;
        if (userObj.only_network_users && isNetworkConnected) return true;

        method.networkErrMessage = userObj.only_network_users
          ? "cart.cart-components.payment-methods.must-connect-to-network"
          : "cart.cart-components.payment-methods.not-available-for-logged-in-users";
        return true;
      }
    }

    if (isOnlyTakeaway && deliveryMethod === "takeaway") {
      if (isTakeawayVisitor) {
        const visitor = method.takeaway_visitors || {};
        if (visitor.all_visitors) return true;
        if (visitor.only_network_visitors && isNetworkConnected) return true;

        method.networkErrMessage = visitor.only_network_visitors
          ? "cart.cart-components.payment-methods.must-connect-to-network"
          : "cart.cart-components.payment-methods.not-available-for-visitors";
        return true;
      } else {
        // 86caa4y7y backend parity (dm-mo-calculate-final-price/
        // paymentGatewaysValidations.js): for normal logged-in takeaway users,
        // an absent or flagless takeaway_users map means unrestricted, so allow
        // without setting a not-available message. Only an explicit
        // only_network_users restriction (while disconnected) keeps a message.
        const userObj = method.takeaway_users || {};
        if (_.isEmpty(userObj)) return true;
        if (userObj.all_users) return true;
        if (userObj.only_network_users && isNetworkConnected) return true;
        if (!userObj.all_users && !userObj.only_network_users) return true;

        method.networkErrMessage = userObj.only_network_users
          ? "cart.cart-components.payment-methods.must-connect-to-network"
          : "cart.cart-components.payment-methods.not-available-for-logged-in-users";
        return true;
      }
    }

    if (isOnlyDelivery && deliveryMethod === "delivery") {
      if (isDeliveryVisitor) {
        const visitor = method.delivery_visitors || {};
        if (visitor.all_visitors) return true;
        if (visitor.only_network_visitors && isNetworkConnected) return true;

        method.networkErrMessage = visitor.only_network_visitors
          ? "cart.cart-components.payment-methods.must-connect-to-network"
          : "cart.cart-components.payment-methods.not-available-for-visitors";
        return true;
      }

      // Missing Delivery user settings preserve the normal logged-in Delivery
      // flow. Once configured, both false is an explicit denial.
      const userObj = method.delivery_users;
      if (!userObj || userObj.all_users) return true;
      if (userObj.only_network_users && isNetworkConnected) return true;

      method.networkErrMessage = userObj.only_network_users
        ? "cart.cart-components.payment-methods.must-connect-to-network"
        : "cart.cart-components.payment-methods.not-available-for-logged-in-users";
      return true;
    }

    return true;
  }

  // Mirror of the server guard (dm-mo-create-order hasNonCashAlternative): the
  // new-customer cash policy applies only when the store actually offers this
  // order a usable NON-cash method. If cash is the only option, blocking it
  // would leave the customer unable to order at all, so the gate is skipped.
  // Conservative: an uncertain gateway still counts as an alternative, which
  // keeps the protection ON. pre_cash (pay at cashier) is NOT cash and DOES
  // count. [86cb07qhh]
  private hasNonCashAlternative(deliveryMethod: string): boolean {
    return _.some(this.cartPaymentGateWays || {}, (gateway, methodKey) => {
      if (!gateway || methodKey === "cash") {
        return false;
      }
      if (gateway.disable_payment_gateway) {
        return false;
      }

      const restrictedModes = [];
      if (gateway.only_delivery) restrictedModes.push("delivery");
      if (gateway.only_takeaway) restrictedModes.push("takeaway");
      if (gateway.only_dinein) restrictedModes.push("dinein");

      if (
        restrictedModes.length &&
        restrictedModes.indexOf(deliveryMethod) === -1
      ) {
        return false;
      }

      return true;
    });
  }

  private isNewCustomerWithNoFulfilledOrder() {
    // Mirror the backend anti-prank signal (dm-mo-create-order): a customer is
    // "new" for the cash gate when they have NO FULFILLED prior order (status
    // complete/processing). A just-placed/pending order does not make them
    // returning. While history is still loading (null/undefined) fail open — the
    // server remains the source of truth. (The servers additionally require the
    // fulfilled order to be older than a recency window and read history by
    // mobile_group, while this list comes from renew_token by customer_id_group;
    // status is the closest client-side match.)
    if (!Array.isArray(this.customerOrders)) {
      return false;
    }
    return !this.customerOrders.some(
      (order) =>
        order && (order.status === "complete" || order.status === "processing")
    );
  }

  private isMethodDisplayable(method) {
    return (
      method &&
      method.active &&
      !method.disable_payment_gateway &&
      !method.hideForDeliveryMethod &&
      !method.deliveryMethodErrMsg &&
      !method.networkErrMessage
    );
  }

  private isBlockedByDelivery(method) {
    return !!(
      method?.hideForDeliveryMethod ||
      method?.deliveryMethodErrMsg ||
      method?.networkErrMessage
    );
  }

  private parseAmount(value: any): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsedValue = parseFloat(value.toString().replace(",", "."));
    if (Number.isNaN(parsedValue)) {
      return null;
    }
    return parsedValue;
  }

  private applyMaxAmountMessages() {
    _.each(this.cartPaymentGateWays, (method) => {
      if (!method) {
        return;
      }

      delete method.maxAmountErrMsg;

      const gatewayMaxAmount = this.parseAmount(method.mo_max_amount);
      if (
        gatewayMaxAmount !== null &&
        gatewayMaxAmount > 0 &&
        this.grandTotal !== null &&
        this.grandTotal > gatewayMaxAmount
      ) {
        method.maxAmountErrMsg =
          "cart.cart-components.payment-methods.gateway-max-amount-short-message";
      }
    });
  }

  private applyMinAmountMessages() {
    _.each(this.cartPaymentGateWays, (method) => {
      if (!method) {
        return;
      }

      delete method.minAmountErrMsg;

      const gatewayMinAmount = this.parseAmount(method.mo_min_amount);
      if (
        gatewayMinAmount !== null &&
        gatewayMinAmount > 0 &&
        this.grandTotal !== null &&
        this.grandTotal < gatewayMinAmount
      ) {
        method.minAmountErrMsg =
          "cart.cart-components.payment-methods.gateway-min-amount-short-message";
      }
    });
  }

  private applyPrePaymentOverrides() {
    const cash = this.cartPaymentGateWays?.cash;
    const preCash = this.cartPaymentGateWays?.pre_cash;
    if (cash && preCash) {
      if (this.isMethodDisplayable(preCash) && this.isBlockedByDelivery(cash)) {
        cash.hideForDeliveryMethod = true;
      }
      if (this.isMethodDisplayable(cash) && this.isBlockedByDelivery(preCash)) {
        preCash.hideForDeliveryMethod = true;
      }
    }

    const mobilePOS = this.cartPaymentGateWays?.mobilePOS;
    const preMobilePOS = this.cartPaymentGateWays?.pre_mobilePOS;
    if (preMobilePOS && mobilePOS) {
      if (
        this.isMethodDisplayable(mobilePOS) &&
        this.isBlockedByDelivery(preMobilePOS)
      ) {
        preMobilePOS.hideForDeliveryMethod = true;
      }
      if (
        this.isMethodDisplayable(preMobilePOS) &&
        this.isBlockedByDelivery(mobilePOS)
      ) {
        mobilePOS.hideForDeliveryMethod = true;
      }
    }
  }

  private addPaymentSettlementPendingMethod() {
    if (!this.filteredCartPaymentGateWays) {
      return;
    }

    if (this.filteredCartPaymentGateWays[this.paymentSettlementPendingMethodID]) {
      delete this.filteredCartPaymentGateWays[
        this.paymentSettlementPendingMethodID
      ];
    }

    if (!this.canShowPaymentSettlementPendingMethod()) {
      return;
    }

    const cashPaymentMethod = this.filteredCartPaymentGateWays?.cash;
    if (!cashPaymentMethod) {
      return;
    }

    this.filteredCartPaymentGateWays[this.paymentSettlementPendingMethodID] =
      _.extend(_.cloneDeep(cashPaymentMethod), {
        renderLikeCash: true,
        sourcePaymentMethodKey: "cash",
        labelTranslationKey:
          "cart.cart-components.payment-methods.paymentSettlementPending",
      });
  }

  private applyPaymentGatewayDisplayLabels() {
    applyMobileorderPaymentDisplayLabels(
      this.filteredCartPaymentGateWays,
      this.selectedLanguage,
      this.moBaseLanguage
    );
    this.syncDisplaySelectedPaymentMethodLabel();
  }

  private getPaymentMethodSourceKey(paymentMethod, key: string) {
    return paymentMethod?.sourcePaymentMethodKey || key;
  }

  private updatePaymentMethodOrder() {
    const configuredPaymentMethodOrder = Array.isArray(
      this.storeInfo?.paymentGatewaysOrder
    )
      ? this.storeInfo.paymentGatewaysOrder
      : [];
    const basePaymentMethodOrder = configuredPaymentMethodOrder.length
      ? configuredPaymentMethodOrder
      : this.paymentMethodOrder;
    const paymentMethodOrder = _.filter(
      _.uniq([...basePaymentMethodOrder, ...this.paymentMethodOrder]),
      (paymentMethodID) => {
        return paymentMethodID !== this.paymentSettlementPendingMethodID;
      }
    );
    const cashIndex = paymentMethodOrder.indexOf("cash");

    if (cashIndex !== -1) {
      paymentMethodOrder.splice(
        cashIndex + 1,
        0,
        this.paymentSettlementPendingMethodID
      );
    } else {
      paymentMethodOrder.push(this.paymentSettlementPendingMethodID);
    }

    this.resolvedPaymentMethodOrder = paymentMethodOrder;
  }

  private canShowPaymentSettlementPendingMethod() {
    return !!(
      this.currentUser?.uposMoUser &&
      this.cartPaymentGateWays?.cash?.paymentSettlementPending
    );
  }

  private isPaymentSettlementPendingSelected() {
    if (!navigator.cookieEnabled) {
      return false;
    }

    return (
      window.localStorage.getItem(this.paymentSettlementPendingStorageKey) ===
        "true"
    );
  }

  private setPaymentSettlementPendingSelected(selected: boolean) {
    if (!navigator.cookieEnabled) {
      return;
    }

    if (selected) {
      window.localStorage.setItem(this.paymentSettlementPendingStorageKey, "true");
    } else {
      window.localStorage.removeItem(this.paymentSettlementPendingStorageKey);
    }
  }

  private syncDisplaySelectedPaymentMethodID() {
    if (!this.filteredCartPaymentGateWays) {
      this.displaySelectedPaymentMethodID = _.cloneDeep(
        this.selectedPaymentMethodID
      );
      this.syncDisplaySelectedPaymentMethodLabel();
      return;
    }

    const canShowPaymentSettlementPendingMethod =
      this.canShowPaymentSettlementPendingMethod() &&
      !!this.filteredCartPaymentGateWays?.[this.paymentSettlementPendingMethodID];

    if (
      this.selectedPaymentMethodID === "cash" &&
      canShowPaymentSettlementPendingMethod &&
      this.isPaymentSettlementPendingSelected()
    ) {
      this.displaySelectedPaymentMethodID = this.paymentSettlementPendingMethodID;
      this.syncDisplaySelectedPaymentMethodLabel();
      return;
    }

    if (
      this.selectedPaymentMethodID !== "cash" ||
      !canShowPaymentSettlementPendingMethod
    ) {
      this.setPaymentSettlementPendingSelected(false);
    }

    this.displaySelectedPaymentMethodID = _.cloneDeep(
      this.selectedPaymentMethodID
    );
    this.syncDisplaySelectedPaymentMethodLabel();
  }

  private syncDisplaySelectedPaymentMethodLabel() {
    const displayPaymentMethod =
      this.filteredCartPaymentGateWays &&
      this.displaySelectedPaymentMethodID &&
      this.filteredCartPaymentGateWays[this.displaySelectedPaymentMethodID];

    // labelTranslationKey keeps priority over a store custom label, mirroring the
    // template precedence: the synthetic cash_paymentSettlementPending row clones
    // cash and would otherwise inherit cash's mobileorderLabel.
    this.displaySelectedPaymentMethodLabel =
      displayPaymentMethod?.labelTranslationKey
        ? null
        : displayPaymentMethod?.mobileorderDisplayLabel || null;
  }

  paymentMethodValidationsCart(writeToState) {
    let validationObj = {
      id: "payments-methods",
      valid: true,
      errorMessages: [],
    };
    this.valid = true;
    if (!this.selectedPaymentMethodID) {
      validationObj.valid = false;
      validationObj.errorMessages.push(
        this.translateService.instant(
          "cart.cart-components.payment-methods.please-select-payment-method"
        )
      );
      this.valid = false;
    }
    if (writeToState) {
      this.store.dispatch(
        new CompleteOrderValidationActions.AddUpdateCompleteOrderValidation(
          _.cloneDeep(validationObj)
        )
      );
    }
  }

  async openReservationAppSelectPaymentMethodModal() {
    if (!this.reservationAppSelectionModal) {
      this.reservationAppSelectionModal = true;

      this.reservationAppSelectionModal = await this.modalCtrl.create({
        component: ReservationSelectPaymentMethodModalComponent,
        cssClass: "reservation-select-payment-method-modal",
        animated: true,
        componentProps: {},
        backdropDismiss: false,
      });

      await this.reservationAppSelectionModal.present();
      this.reservationAppSelectionModal.onDidDismiss().then(async (data) => {
        if (data && data.data) {
          this.selectedPaymentMethodID = _.cloneDeep(data.data);
          this.selectPaymentMethodRadio("");
        }
        this.reservationAppSelectionModal = null;
      });
    }
  }

  async selectPaymentMethodRadio(ev) {
    const selectedPaymentSettlementPendingMethod =
      this.displaySelectedPaymentMethodID ===
        this.paymentSettlementPendingMethodID &&
      this.canShowPaymentSettlementPendingMethod();

    this.selectedPaymentMethodID = selectedPaymentSettlementPendingMethod
      ? "cash"
      : _.cloneDeep(this.displaySelectedPaymentMethodID);

    this.setPaymentSettlementPendingSelected(selectedPaymentSettlementPendingMethod);

    this.store.dispatch(
      new SelectedPaymentMethodIDActions.SetSelectedPaymentMethodID(
        _.cloneDeep(this.selectedPaymentMethodID)
      )
    );

    if (navigator.cookieEnabled) {
      window.localStorage.setItem(
        "selectedPaymentMethodID",
        JSON.stringify(this.selectedPaymentMethodID)
      );
    }

    this.syncDisplaySelectedPaymentMethodID();

    // TO_DO DO WE WANT TO LOAD CUSTOM OPTIONS HERE?

    // $rootScope.loadCustomOptions();
    // if ($rootScope.store_info.cartCustomOptionsActive) {
    //   $rootScope.calculateCartInline(true);
    // }
  }

  checkForOnlineOnlyPaymentPromos() {
    this.subscriptions.push(
      this.store
        .select("checkboxPromotions")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(100))
        .subscribe((state) => {
          if (
            state &&
            state.checkboxPromotions &&
            !_.isEqual(this.checkboxPromotions, state.checkboxPromotions)
          ) {
            this.checkboxPromotions = _.cloneDeep(state.checkboxPromotions);

            if (this.checkboxPromotions && this.checkboxPromotions.length > 0) {
              this.disableAllPaymentSelections = false;
              _.each(this.checkboxPromotions, (promo) => {
                if (promo.onlinePaymentOnly && promo.promoCheckbox) {
                  this.disableAllPaymentSelections = true;
                  if (this.paymentGateways && this.paymentGateways.length > 0) {
                    _.each(this.paymentGateways, (gateway) => {
                      if (gateway.id !== "paypal" && gateway.id !== "klarna") {
                        this.store.dispatch(
                          new SelectedPaymentMethodIDActions.SetSelectedPaymentMethodID(
                            _.cloneDeep(gateway.id)
                          )
                        );
                        return;
                      }
                    });
                  }
                }
              });
            }
          }
        })
    );
  }

  checkForDisablingSomeMethods(key, method, ev) {
    if (
      this.disableAllPaymentSelections &&
      (key == "cash" ||
        method?.renderLikeCash ||
        key === "mobilePOS" ||
        key === "pre_cash" ||
        key === "pre_mobilePOS" ||
        key === "paypal" ||
        key == "klarna")
    ) {
      return true;
    } else {
      if (method.disable_payment_gateway) {
        return true;
      } else {
        return false;
      }
    }
  }

  ngOnDestroy() {
    if (this.dineinTableContextSub) {
      this.dineinTableContextSub.unsubscribe();
    }
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
    if (this.reservationAppSelectionModal) {
      this.reservationAppSelectionModal.dismiss();
      this.reservationAppSelectionModal = null;
    }
  }
}
